import React, { useEffect, useState } from "react";
import { Trash2, Mail, Download, Send, Eye } from "lucide-react";
import { adminApi } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function SubscribersManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [selectedEp, setSelectedEp] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([adminApi.subscribers.list(), adminApi.podcasts.list()])
      .then(([subs, eps]) => { setItems(subs); setEpisodes(eps); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleDelete = async () => {
    try { await adminApi.subscribers.remove(deleteId); toast({ title: "Subscriber removed" }); load(); }
    catch { toast({ title: "Error removing", variant: "destructive" }); }
    finally { setDeleteId(null); }
  };

  const sendBroadcast = async () => {
    setSending(true);
    try {
      const res = await adminApi.podcasts.broadcast(selectedEp, window.location.origin);
      toast({ title: `Email queued to ${res.recipients} subscriber(s)` });
      setSelectedEp("");
      load();
    } catch {
      toast({ title: "Could not send emails", variant: "destructive" });
    } finally {
      setSending(false);
      setConfirmOpen(false);
    }
  };

  const sendPreview = async () => {
    setPreviewing(true);
    try {
      const res = await adminApi.podcasts.broadcastPreview(selectedEp, window.location.origin);
      if (res?.ok) toast({ title: `Preview sent to ${res.email}`, description: "Check your inbox to see exactly what subscribers will get." });
      else toast({ title: "Couldn't send preview", description: res?.result?.error || "Email isn't configured on the server.", variant: "destructive" });
    } catch {
      toast({ title: "Couldn't send preview", variant: "destructive" });
    } finally {
      setPreviewing(false);
    }
  };

  const selectedEpObj = episodes.find((e) => e.id === selectedEp);

  const exportCsv = () => {
    const rows = [["Name", "Email", "Source", "Subscribed"], ...items.map((s) => [s.name || "", s.email, s.source || "Podcast", fmt(s.created_at)])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "podcast-subscribers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-navy">Podcast Subscribers</h1>
          <p className="text-sm text-slate-500">{items.length} people signed up for new-episode emails.</p>
        </div>
        {items.length > 0 && (
          <button data-testid="export-subscribers-btn" onClick={exportCsv} className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold px-5 py-2.5 rounded-full transition-colors text-sm"><Download size={15} /> Export CSV</button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-8">
        <h2 className="font-display font-bold text-navy mb-1 flex items-center gap-2"><Send size={17} className="text-orange" /> Email an episode to subscribers</h2>
        <p className="text-sm text-slate-500 mb-4">Pick an episode and send a "new episode" email to all {items.length} subscriber(s).</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={selectedEp} onValueChange={setSelectedEp}>
            <SelectTrigger data-testid="broadcast-episode-select" className="flex-1"><SelectValue placeholder="Choose an episode" /></SelectTrigger>
            <SelectContent>
              {episodes.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.title}{e.last_emailed_at ? ` — sent ${fmt(e.last_emailed_at)}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            data-testid="broadcast-preview-btn"
            onClick={sendPreview}
            disabled={!selectedEp || previewing}
            className="inline-flex items-center justify-center gap-2 border border-slate-200 text-navy hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed font-bold px-6 py-2.5 rounded-full transition-colors whitespace-nowrap"
          >
            <Eye size={16} /> {previewing ? "Sending…" : "Send me a preview"}
          </button>
          <button
            data-testid="broadcast-send-btn"
            onClick={() => setConfirmOpen(true)}
            disabled={!selectedEp || items.length === 0}
            className="inline-flex items-center justify-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold px-6 py-2.5 rounded-full transition-colors whitespace-nowrap"
          >
            <Send size={16} /> Send to all
          </button>
        </div>
        {selectedEpObj?.last_emailed_at && (
          <p className="text-xs text-amber-600 mt-3">Heads up: this episode was already emailed on {fmt(selectedEpObj.last_emailed_at)}.</p>
        )}
      </div>

      {loading ? <p className="text-slate-400">Loading...</p> : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center text-slate-400">No subscribers yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} data-testid={`subscriber-${s.id}`} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-orange/10 flex items-center justify-center text-orange shrink-0"><Mail size={18} /></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-navy truncate">{s.name || "—"}</p>
                <p className="text-sm text-slate-400 truncate">{s.email}</p>
              </div>
              {s.source && (
                <span className="text-xs font-semibold bg-orange/10 text-orange px-2.5 py-1 rounded-full shrink-0 hidden md:inline-block max-w-[220px] truncate" title={s.source}>{s.source}</span>
              )}
              <span className="text-xs text-slate-400 shrink-0 hidden sm:block">{fmt(s.created_at)}</span>
              <button onClick={() => setDeleteId(s.id)} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send this episode to all subscribers?</AlertDialogTitle>
            <AlertDialogDescription>
              "{selectedEpObj?.title}" will be emailed to <strong>{items.length}</strong> subscriber(s).
              {selectedEpObj?.last_emailed_at ? ` This episode was already sent on ${fmt(selectedEpObj.last_emailed_at)}.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="broadcast-confirm-btn" onClick={sendBroadcast} disabled={sending} className="bg-orange hover:bg-orange-dark rounded-full">{sending ? "Sending..." : "Send now"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove this subscriber?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 rounded-full">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
